/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { useProfile } from '@/contexts/ProfileProvider';
import { useTheme } from '@/contexts/ThemeProvider';
import { CENTRAL_CLIENTE_PER_PAGE } from '@/service/club/api';
import { IContrat, IFaturas } from '@/service/club/types';
import { useGetContract, useGetFaturas } from '@/service/club/hooks';
import { APP_EVENTS, spawnAppEvent } from '@/service/utils';
import { masks } from '@/utils/masks';
import {
  getBoleto,
  getPixFatura,
  fetchCircuitIds,
  trocarCredenciaisRoteador,
  PixFaturaResponse,
} from '../services/faturaApi';
import { useDesbloqueioConfianca } from './useDesbloqueioConfianca';
import { useDesbloqueioHubsoft } from './useDesbloqueioHubsoft';
import { orderByVencimento, orderByContratos, isValidDate, MONTH_NAMES } from '../utils/faturaDate';
import { apiErrorMessage, pageFromLink } from '../utils/listPagination';

function faturaBelongsToContract(fatura: any, contrato: IContrat): boolean {
  const faturaContrato = fatura?.contrato != null ? String(fatura.contrato).trim() : '';
  if (!faturaContrato) return true;

  const num = contrato.numero_contrato != null ? String(contrato.numero_contrato).trim() : '';
  const cod = contrato.codigo_contrato != null ? String(contrato.codigo_contrato).trim() : '';
  const idServico = (contrato as any).id_cliente_servico != null ? String((contrato as any).id_cliente_servico).trim() : '';

  return (
    faturaContrato === num ||
    faturaContrato === cod ||
    faturaContrato === idServico ||
    num === faturaContrato ||
    cod === faturaContrato
  );
}

export function useFaturaPage() {
  const { colors, isApp, customerCentralModule, integrationId, cellphone, contactLink, slug } = useTheme();
  const profile: any = useProfile();
  const toast = useToast();
  const [contratosPage, setContratosPage] = React.useState(1);
  const [faturasPage, setFaturasPage] = React.useState(1);
  const perPage = CENTRAL_CLIENTE_PER_PAGE;

  const contratosQuery = useGetContract({ page: contratosPage, per_page: perPage });
  const faturasQuery = useGetFaturas({ page: faturasPage, per_page: perPage });
  const { data: dataContratos, isLoading, isIdle: isContratosIdle, isFetching: isFetchingContratos } = contratosQuery;
  const { data: dataFaturas, isLoading: IsLoadingFaturas, isIdle: isFaturasIdle, isFetching: isFetchingFaturas } = faturasQuery;

  const [contratos, setContratos] = React.useState<IContrat[]>([]);
  const [faturas, setFaturas] = React.useState<any[]>([]);
  const [lastFatura, setLastFatura] = React.useState<IFaturas>();
  const [lastContrato, setLastContrato] = React.useState<IContrat>();

  const [segundaVia, setSegundaVia] = React.useState<{
    link_segunda_via: string | null;
    observacao: string | null;
  }>({ link_segunda_via: null, observacao: null });
  const [isLoadingSegundaVia, setIsLoadingSegundaVia] = React.useState(false);

  const [pix, setPix] = React.useState<PixFaturaResponse | null>(null);
  const [pixError, setPixError] = React.useState<string | null>(null);
  const [isLoadingPix, setIsLoadingPix] = React.useState(false);
  const [isPixModalOpen, setIsPixModalOpen] = React.useState(false);

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isRouterCredentialsModalOpen, setIsRouterCredentialsModalOpen] = React.useState(false);

  const [circuitIds, setCircuitIds] = React.useState<Array<{ value: string; label: string }>>([]);
  const [isLoadingCircuitIds, setIsLoadingCircuitIds] = React.useState(false);
  const [isSubmittingCredentials, setIsSubmittingCredentials] = React.useState(false);

  const { executeDesbloqueioConfianca } = useDesbloqueioConfianca();
  const desbloqueioHubsoft = useDesbloqueioHubsoft({
    idClienteServico: (lastContrato as any)?.id_cliente_servico,
  });

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const currentMonthRef = React.useRef<HTMLDivElement | null>(null);
  const hasAutoScrolledRef = React.useRef(false);

  const handleChangeFatura = useCallback(
    async (fatura: any) => {
      setLastFatura(fatura);
      if (slug === 'newoesteconnect' && fatura?.contrato != null) {
        const contratoMatch = contratos.find((c) => faturaBelongsToContract(fatura, c));
        if (contratoMatch && contratoMatch !== lastContrato) {
          setLastContrato(contratoMatch);
          toast({
            title: 'Contrato alterado',
            description: 'Esta fatura pertence a outro contrato. O contrato foi atualizado automaticamente.',
            status: 'info',
            duration: 5000,
            isClosable: true,
          });
        }
      }
      if (integrationId && ['mk', 'ixc', 'top', 'voalleThird'].includes(integrationId)) {
        setIsLoadingSegundaVia(true);
        try {
          const response = await getBoleto(fatura.codigo_fatura);
          setSegundaVia(response);
        } finally {
          setIsLoadingSegundaVia(false);
        }
      }
    },
    [integrationId, slug, contratos, lastContrato]
  );

  const handleOpenLink = useCallback(
    (url: string) => {
      if (isApp) spawnAppEvent(APP_EVENTS.OPEN_LINK, url);
      else window.open(url, '_blank');
    },
    [isApp]
  );

  // Link de redirecionamento (outro canal ou WhatsApp próprio) tem prioridade sobre o celular.
  // Fallback para cellphone com link: convênios salvos antes da separação dos campos.
  const rawLink = contactLink?.trim() || (cellphone && /[a-z]/i.test(cellphone) ? cellphone : '');
  const whatsappLink = rawLink ? (/^https?:\/\//i.test(rawLink) ? rawLink : `https://${rawLink}`) : null;

  const handleOpenWhatsApp = useCallback(() => {
    if (whatsappLink) return handleOpenLink(whatsappLink);
    const phoneNumber = cellphone ? masks.whatsapp(cellphone) : '5531984011893';
    const message = 'Olá, gostaria de ajuda com minha fatura';
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    handleOpenLink(url);
  }, [cellphone, whatsappLink, handleOpenLink]);

  const handleOpenWhatsAppPlan = useCallback(() => {
    if (whatsappLink) return handleOpenLink(whatsappLink);
    const phoneNumber = cellphone ? masks.whatsapp(cellphone) : '5531984011893';
    const contractId = (lastContrato as any)?.numero_contrato ?? lastContrato?.codigo_contrato;
    const message = `Olá, gostaria de ajuda com meu contrato ${contractId}`;
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    handleOpenLink(url);
  }, [cellphone, whatsappLink, lastContrato, handleOpenLink]);

  const handleOpenRouterCredentialsModal = useCallback(async () => {
    setIsRouterCredentialsModalOpen(true);
    setIsLoadingCircuitIds(true);
    try {
      const ids = await fetchCircuitIds();
      setCircuitIds(ids);
    } catch (error: any) {
      toast({
        title: 'Erro ao buscar Circuit IDs',
        description: error?.response?.data?.error || 'Não foi possível carregar os Circuit IDs',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setIsLoadingCircuitIds(false);
    }
  }, [toast]);

  const onSubmitRouterCredentials = useCallback(
    async (formData: { circuit_id_integration: string; wifi_ssid: string; wifi_password: string }) => {
      setIsSubmittingCredentials(true);
      try {
        await trocarCredenciaisRoteador(formData);
        toast({
          title: 'Credenciais atualizadas com sucesso',
          description: 'As credenciais do roteador foram alteradas.',
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
        setIsRouterCredentialsModalOpen(false);
      } catch (error: any) {
        toast({
          title: 'Erro ao alterar credenciais',
          description: error?.response?.data?.error || 'Não foi possível alterar as credenciais do roteador',
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
      } finally {
        setIsSubmittingCredentials(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    if (faturasQuery.isError) {
      setFaturas([]);
      return;
    }
    if (!dataFaturas) return;
    if (!dataFaturas.results || dataFaturas.results.result === false) {
      setFaturas([]);
      return;
    }
    const dataFaturasAux: any[] = dataFaturas.results.Faturas || [];
    if (dataFaturasAux.length === 0) {
      setFaturas([]);
      return;
    }
    const ordered = orderByVencimento(dataFaturasAux);
    setFaturas(ordered);
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const currentMonthFatura = ordered.find((f: any) => {
      if (!isValidDate(f.vencimento)) return false;
      const [, month, year] = f.vencimento.split('/');
      return Number(month) - 1 === currentMonth && Number(year) === currentYear;
    });
    handleChangeFatura(currentMonthFatura || ordered[ordered.length - 1]);
  }, [dataFaturas, faturasQuery.isError]);

  useEffect(() => {
    if (contratosQuery.isError || !dataContratos) {
      if (contratosQuery.isError) setContratos([]);
      return;
    }
    if (!dataContratos.results || dataContratos.results.result === false) {
      setContratos([]);
      return;
    }
    const ordered = orderByContratos(dataContratos.results.Contratos || []);
    setContratos(ordered);
    if (ordered.length > 0) {
      setLastContrato((current) => current ?? ordered[ordered.length - 1]);
    }
  }, [dataContratos, contratosQuery.isError]);

  const contratosFailed = contratosQuery.isError || dataContratos?.results?.result === false;
  const faturasFailed = faturasQuery.isError || dataFaturas?.results?.result === false;
  const contratosCount = contratosFailed ? 0 : dataContratos?.count ?? 0;
  const faturasCount = faturasFailed ? 0 : dataFaturas?.count ?? 0;
  const contratosErrorMessage = contratosQuery.isError
    ? apiErrorMessage(contratosQuery.error)
    : dataContratos?.results?.result === false
      ? dataContratos.results.error
      : '';
  const faturasErrorMessage = faturasQuery.isError
    ? apiErrorMessage(faturasQuery.error)
    : dataFaturas?.results?.result === false
      ? dataFaturas.results.error
      : '';

  const goToPreviousContratos = useCallback(() => {
    if (!dataContratos?.previous) return;
    setContratosPage(pageFromLink(dataContratos.previous) ?? Math.max(1, contratosPage - 1));
  }, [contratosPage, dataContratos?.previous]);

  const goToNextContratos = useCallback(() => {
    if (!dataContratos?.next) return;
    setContratosPage(pageFromLink(dataContratos.next) ?? contratosPage + 1);
  }, [contratosPage, dataContratos?.next]);

  const goToPreviousFaturas = useCallback(() => {
    if (!dataFaturas?.previous) return;
    setFaturasPage(pageFromLink(dataFaturas.previous) ?? Math.max(1, faturasPage - 1));
  }, [dataFaturas?.previous, faturasPage]);

  const goToNextFaturas = useCallback(() => {
    if (!dataFaturas?.next) return;
    setFaturasPage(pageFromLink(dataFaturas.next) ?? faturasPage + 1);
  }, [dataFaturas?.next, faturasPage]);

  const hasSegundaVia =
    lastFatura?.link_segunda_via ||
    segundaVia?.link_segunda_via ||
    segundaVia?.observacao;

  const handleSegundaViaClick = useCallback(() => {
    if (!lastFatura || !hasSegundaVia) {
      toast({
        title: 'Não há fatura disponível',
        description: 'Não foi possível encontrar uma fatura para gerar a segunda via.',
        status: 'warning',
        duration: 9000,
        isClosable: true,
      });
      return;
    }
    const link = lastFatura?.link_segunda_via || segundaVia?.link_segunda_via;
    if (link) {
      if (lastFatura.fatura_paga) {
        toast({
          title: 'Esta fatura já foi quitada',
          description: 'Selecione uma fatura em aberto para visualizar as opções de pagamento.',
          status: 'info',
          duration: 9000,
          isClosable: true,
        });
        return;
      }
      handleOpenLink(link);
      return;
    }
    if (segundaVia?.observacao) {
      toast({ title: segundaVia.observacao, status: 'info', duration: 9000, isClosable: true });
    }
  }, [lastFatura, hasSegundaVia, segundaVia, toast, handleOpenLink]);

  const hasPix = !!integrationId && ['ixc', 'voalle', 'voalleThird'].includes(integrationId);

  const handlePixClick = useCallback(async () => {
    if (!lastFatura) {
      toast({
        title: 'Não há fatura disponível',
        description: 'Não foi possível encontrar uma fatura para gerar o Pix.',
        status: 'warning',
        duration: 9000,
        isClosable: true,
      });
      return;
    }
    if (lastFatura.fatura_paga) {
      toast({
        title: 'Esta fatura já foi quitada',
        description: 'Selecione uma fatura em aberto para pagar com Pix.',
        status: 'info',
        duration: 9000,
        isClosable: true,
      });
      return;
    }

    setPix(null);
    setPixError(null);
    setIsPixModalOpen(true);
    setIsLoadingPix(true);
    try {
      const response = await getPixFatura(lastFatura.codigo_fatura);
      const hasCode = !!response.pix_copia_cola;
      const hasImage = !!response.pix_qrcode_base64 || !!response.pix_qrcode_url;
      if (response.result === false || (!hasCode && !hasImage)) {
        setPixError(response.error || 'Não foi possível gerar o Pix desta fatura. Tente novamente mais tarde.');
        return;
      }
      setPix(response);
    } catch (error: any) {
      setPixError(
        error?.response?.data?.error || 'Não foi possível gerar o Pix desta fatura. Tente novamente mais tarde.'
      );
    } finally {
      setIsLoadingPix(false);
    }
  }, [lastFatura, toast]);

  const closePixModal = useCallback(() => {
    setIsPixModalOpen(false);
    setPix(null);
    setPixError(null);
  }, []);

  const showBoxesFaturaData = React.useMemo(() => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const faturasDoContrato =
      lastContrato != null
        ? slug !== 'newoesteconnect' ? faturas.filter((f: any) => faturaBelongsToContract(f, lastContrato)) : faturas
        : faturas;

    const faturasParaExibir =
      lastContrato != null && faturasDoContrato.length > 0 ? faturasDoContrato : faturas;

    const filtered = faturasParaExibir.filter((fatura: any) => {
      if (!isValidDate(fatura.vencimento)) return false;
      const [, month, year] = fatura.vencimento.split('/');
      const faturaMonth = Number(month) - 1;
      const faturaYear = Number(year);
      const faturaDate = new Date(faturaYear, faturaMonth, 1);
      const nextMonthDate = new Date(currentYear, currentMonth + 1, 1);
      return faturaDate <= nextMonthDate;
    });

    return filtered
      .map((fatura: any) => {
        let isCurrentMonth = false;
        let formattedTitle = 'Data inválida';
        if (isValidDate(fatura.vencimento)) {
          try {
            const [, month, year] = fatura.vencimento.split('/');
            const faturaMonth = Number(month) - 1;
            const faturaYear = Number(year);
            isCurrentMonth = faturaMonth === currentMonth && faturaYear === currentYear;
            formattedTitle = `${MONTH_NAMES[faturaMonth]} ${faturaYear}`;
          } catch {
            formattedTitle = 'Data inválida';
          }
        }
        return {
          fatura,
          formattedTitle,
          isCurrentMonth,
          isActive: lastFatura?.codigo_fatura === fatura.codigo_fatura,
        };
      })
      .reverse();
  }, [faturas, lastFatura, lastContrato]);

  return {
    colors,
    isApp,
    slug,
    customerCentralModule,
    integrationId,
    isLoading: isLoading || isContratosIdle,
    IsLoadingFaturas: IsLoadingFaturas || isFaturasIdle,
    isFetchingContratos,
    isFetchingFaturas,
    perPage,
    contratosPage,
    faturasPage,
    contratosCount,
    faturasCount,
    contratosFailed,
    faturasFailed,
    contratosErrorMessage,
    faturasErrorMessage,
    contratosHasPrevious: contratosPage > 1 && !!dataContratos?.previous,
    contratosHasNext: !!dataContratos?.next,
    faturasHasPrevious: faturasPage > 1 && !!dataFaturas?.previous,
    faturasHasNext: !!dataFaturas?.next,
    goToPreviousContratos,
    goToNextContratos,
    goToPreviousFaturas,
    goToNextFaturas,
    isLoadingSegundaVia,
    hasPix,
    pix,
    pixError,
    isLoadingPix,
    isPixModalOpen,
    contratos,
    faturas,
    lastFatura,
    lastContrato,
    segundaVia,
    isModalOpen,
    isRouterCredentialsModalOpen,
    circuitIds,
    isLoadingCircuitIds,
    isSubmittingCredentials,
    containerRef,
    currentMonthRef,
    hasAutoScrolledRef,
    profile,
    setIsModalOpen,
    handleChangeFatura,
    handleSegundaViaClick,
    handlePixClick,
    closePixModal,
    handleOpenWhatsApp,
    handleOpenWhatsAppPlan,
    handleOpenLink,
    handleOpenRouterCredentialsModal,
    executeDesbloqueioConfianca,
    desbloqueioHubsoft,
    onSubmitRouterCredentials,
    showBoxesFaturaData,
    hasSegundaVia,
    closeContratosModal: () => setIsModalOpen(false),
    closeRouterCredentialsModal: () => setIsRouterCredentialsModalOpen(false),
    handleSelectContrato: useCallback(
      (contrato: IContrat) => {
        setLastContrato(contrato);
        const faturasDoContrato = faturas.filter((f: any) => faturaBelongsToContract(f, contrato));
        const faturasParaUsar = faturasDoContrato.length > 0 ? faturasDoContrato : faturas;
        const ordered = orderByVencimento(faturasParaUsar);
        if (ordered.length > 0) {
          const currentDate = new Date();
          const currentMonth = currentDate.getMonth();
          const currentYear = currentDate.getFullYear();
          const currentMonthFatura = ordered.find((f: any) => {
            if (!isValidDate(f.vencimento)) return false;
            const [, month, year] = f.vencimento.split('/');
            return Number(month) - 1 === currentMonth && Number(year) === currentYear;
          });
          handleChangeFatura(currentMonthFatura || ordered[ordered.length - 1]);
        } else {
          setLastFatura(undefined);
        }
      },
      [faturas, handleChangeFatura]
    ),
  };
}
